import React, { useEffect, useState, useCallback, useRef } from "react";
import { MessageCircle, Send, Mail, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const POLL_MS = 5000;

const fmtTime = (iso) => {
  try {
    return new Date(iso).toLocaleString([], { dateStyle: "short", timeStyle: "short" });
  } catch { return iso; }
};

/**
 * Floating live-chat inbox for agents. Sits in the bottom-right corner of the
 * agent dashboard. Polls /api/agent/chat-sessions every few seconds and shows a
 * red unread-count badge.
 */
const AgentChatInbox = () => {
  const { api } = useAuth();
  const [open, setOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const scrollerRef = useRef(null);

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await api.get("/agent/chat-sessions");
      setSessions(data);
    } catch (err) {
      /* ignore transient */
    }
  }, [api]);

  const loadMessages = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/agent/chat-sessions/${id}/messages`);
      setMessages(data);
    } catch (err) { /* ignore */ }
  }, [api]);

  useEffect(() => {
    loadSessions();
    const t = setInterval(loadSessions, POLL_MS);
    return () => clearInterval(t);
  }, [loadSessions]);

  useEffect(() => {
    if (!activeId) return;
    loadMessages(activeId);
    const t = setInterval(() => loadMessages(activeId), POLL_MS);
    return () => clearInterval(t);
  }, [activeId, loadMessages]);

  useEffect(() => {
    if (open && scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, open]);

  // When the agent opens a session, refresh sessions list shortly after so the
  // unread badge clears.
  useEffect(() => {
    if (activeId) {
      const t = setTimeout(loadSessions, 500);
      return () => clearTimeout(t);
    }
  }, [activeId, loadSessions]);

  const send = async (e) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body || !activeId) return;
    setDraft("");
    try {
      const { data } = await api.post(`/agent/chat-sessions/${activeId}/messages`, { body });
      setMessages((prev) => [...prev, data]);
      loadSessions();
    } catch (err) { console.error(err); }
  };

  const totalUnread = sessions.reduce((sum, s) => sum + (s.unread_for_agent || 0), 0);

  return (
    <>
      <button
        data-testid="agent-chat-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-label="Open live chat inbox"
        style={{
          position: "fixed", bottom: 96, right: 20,
          width: 60, height: 60, borderRadius: 30,
          background: "#B68A35", color: "#fff", border: "none",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 18px 36px -12px rgba(0,0,0,0.45)",
          cursor: "pointer", zIndex: 10000,
        }}
      >
        {open ? <X size={22} /> : <MessageCircle size={24} strokeWidth={1.8} />}
        {totalUnread > 0 && !open && (
          <span style={{
            position: "absolute", top: 2, right: 2,
            background: "#e11d48", color: "#fff",
            fontSize: 11, fontWeight: 700,
            minWidth: 20, height: 20, borderRadius: 10,
            padding: "0 6px", display: "inline-flex", alignItems: "center", justifyContent: "center",
            border: "2px solid #05070A",
          }}>{totalUnread}</span>
        )}
      </button>

      {open && (
        <div
          data-testid="agent-chat-panel"
          style={{
            position: "fixed", bottom: 168, right: 20,
            width: 560, maxWidth: "calc(100vw - 40px)",
            height: 560, maxHeight: "calc(100vh - 200px)",
            background: "#0A0E17", border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 16, display: "flex", overflow: "hidden",
            boxShadow: "0 30px 80px -20px rgba(0,0,0,0.65)", zIndex: 10000,
            color: "#F8F9FA",
            fontFamily: "Manrope, system-ui, sans-serif",
          }}
        >
          {/* Sessions list */}
          <div style={{ width: 220, borderRight: "1px solid rgba(255,255,255,0.06)", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "14px 16px", background: "#0F2540", color: "#fff", fontSize: 13, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase" }}>
              Live chats
            </div>
            <div style={{ flex: 1, overflowY: "auto" }}>
              {sessions.length === 0 && (
                <div style={{ padding: 20, color: "#5B6B7E", fontSize: 13, textAlign: "center" }}>
                  No chats yet.
                </div>
              )}
              {sessions.map((s) => (
                <button
                  key={s.id}
                  data-testid="agent-chat-session-row"
                  onClick={() => setActiveId(s.id)}
                  style={{
                    width: "100%", textAlign: "left",
                    padding: "12px 14px", border: "none",
                    borderBottom: "1px solid rgba(255,255,255,0.05)",
                    background: activeId === s.id ? "rgba(182,138,53,0.12)" : "transparent",
                    color: "inherit", cursor: "pointer",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{s.visitor_name}</span>
                    {s.unread_for_agent > 0 && (
                      <span style={{
                        background: "#e11d48", color: "#fff",
                        fontSize: 10, fontWeight: 700,
                        minWidth: 18, height: 18, borderRadius: 9,
                        padding: "0 5px",
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                      }}>{s.unread_for_agent}</span>
                    )}
                  </div>
                  {s.visitor_email && (
                    <div style={{ fontSize: 10, color: "#8B95A5", marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}>
                      <Mail size={9} /> {s.visitor_email}
                    </div>
                  )}
                  <div style={{ fontSize: 10, color: "#8B95A5", marginTop: 4 }}>
                    {fmtTime(s.last_activity)}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Conversation */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
            {!activeId ? (
              <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#5B6B7E", fontSize: 13, padding: 24, textAlign: "center" }}>
                Select a chat from the left to start replying.
              </div>
            ) : (
              <>
                <div ref={scrollerRef} style={{ flex: 1, overflowY: "auto", padding: 16, background: "#05070A" }}>
                  {messages.length === 0 && (
                    <div style={{ textAlign: "center", color: "#5B6B7E", fontSize: 13, padding: 12 }}>
                      No messages yet in this chat.
                    </div>
                  )}
                  {messages.map((m) => {
                    const mine = m.from_role === "agent";
                    return (
                      <div key={m.id} style={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start", marginBottom: 8 }}>
                        <div style={{
                          maxWidth: "82%",
                          background: mine ? "#B68A35" : "rgba(255,255,255,0.06)",
                          color: mine ? "#05070A" : "#F8F9FA",
                          borderRadius: 12, padding: "8px 12px", fontSize: 13, lineHeight: 1.45,
                        }}>
                          {!mine && (
                            <div style={{ fontSize: 9, color: "#B68A35", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 2 }}>
                              {m.from_name} · Visitor
                            </div>
                          )}
                          <div style={{ whiteSpace: "pre-wrap" }}>{m.body}</div>
                          <div style={{ fontSize: 9, opacity: 0.55, marginTop: 4, textAlign: "right" }}>{fmtTime(m.created_at)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <form onSubmit={send} style={{ borderTop: "1px solid rgba(255,255,255,0.08)", padding: 10, display: "flex", gap: 8, background: "#0A0E17" }}>
                  <input
                    data-testid="agent-chat-input"
                    value={draft} onChange={(e) => setDraft(e.target.value)}
                    placeholder="Type your reply…"
                    style={{
                      flex: 1, padding: "9px 12px", borderRadius: 8,
                      background: "transparent", border: "1px solid rgba(255,255,255,0.12)",
                      color: "#F8F9FA", fontSize: 13, outline: "none",
                    }}
                  />
                  <button
                    data-testid="agent-chat-send" type="submit" disabled={!draft.trim()}
                    style={{
                      background: "#B68A35", color: "#fff", border: "none",
                      padding: "0 14px", borderRadius: 8, cursor: "pointer",
                      fontSize: 13, fontWeight: 600, opacity: draft.trim() ? 1 : 0.4,
                      display: "inline-flex", alignItems: "center", gap: 6,
                    }}
                  >
                    <Send size={13} />
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AgentChatInbox;
